import { Fragment } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, Flex, Heading, LoadingOrbit, SettingsRow, Switch, Text, Box } from '@chatsdet/ui';
import { useAuth } from '../../../contexts/AuthContext.js';
import { API_URL } from '../../../config.js';
import { toApiError } from '../../../lib/api-error';

type PlanFeature = { available: boolean; enabled: boolean };
type PlanFeatures = { previews: PlanFeature; prReviews: PlanFeature };
type PlanFeaturesUpdate = { previews?: boolean; prReviews?: boolean };

const QUERY_KEY = ['org-plan-features'];
const NOT_IN_PLAN = 'Not included in your plan — please talk to the Cofactor team.';

const ROWS: Array<{ key: keyof PlanFeatures; title: string; description: string }> = [
  {
    key: 'previews',
    title: 'Preview environments',
    description: 'A running preview of your app on every pull request.',
  },
  {
    key: 'prReviews',
    title: 'AI PR reviews',
    description: 'Cofactor reviews each pull request and posts a GitHub review.',
  },
];

/**
 * Org plan features (Preview environments, AI PR reviews). Every member sees
 * the current state; only a Cofactor superadmin can change it.
 */
export function PlanFeaturesCard() {
  const { user } = useAuth();
  const canEdit = Boolean(user?.isSuperadmin);
  const queryClient = useQueryClient();

  const featuresQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<{ ok: boolean; data: PlanFeatures }> => {
      const response = await fetch(`${API_URL}/org/plan-features`, { credentials: 'include' });
      if (!response.ok) throw await toApiError(response, 'Failed to load plan features');
      return response.json();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: PlanFeaturesUpdate) => {
      const response = await fetch(`${API_URL}/org/plan-features`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw await toApiError(response, 'Failed to update plan features');
      return response.json() as Promise<{ ok: boolean; data: PlanFeatures }>;
    },
    onSuccess: (result) => {
      queryClient.setQueryData(QUERY_KEY, result);
    },
  });

  const features = featuresQuery.data?.data;

  return (
    <Card padding="lg">
      <CardHeader>
        <Box>
          <Heading level={3}>Plan features</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Features included in this organization&apos;s plan.
          </Text>
        </Box>
      </CardHeader>
      <CardContent>
        {featuresQuery.isLoading ? (
          <Flex align="center" justify="center" className="py-6">
            <LoadingOrbit size="sm" />
          </Flex>
        ) : !features ? (
          <Text size="sm" className="text-destructive">
            Failed to load plan features.
          </Text>
        ) : (
          <Flex direction="column" gap="md">
            {ROWS.map((row) => {
              const feature = features[row.key];
              const interactive = canEdit && feature.available && !updateMutation.isPending;
              return (
                <Fragment key={row.key}>
                  <SettingsRow
                    title={row.title}
                    description={row.description}
                    onPress={
                      interactive
                        ? () => updateMutation.mutate({ [row.key]: !feature.enabled })
                        : undefined
                    }
                  >
                    <Switch
                      aria-label={row.title}
                      isDisabled={!interactive}
                      isSelected={feature.enabled}
                      onChange={(isSelected) => updateMutation.mutate({ [row.key]: isSelected })}
                    />
                  </SettingsRow>
                  {!canEdit && !feature.enabled ? (
                    <Text size="sm" className="text-muted-foreground">
                      {NOT_IN_PLAN}
                    </Text>
                  ) : null}
                  {canEdit && !feature.available ? (
                    <Text size="sm" className="text-muted-foreground">
                      Not available to this organization yet.
                    </Text>
                  ) : null}
                </Fragment>
              );
            })}
            {updateMutation.isError ? (
              <Text size="sm" className="text-destructive">
                {updateMutation.error.message}
              </Text>
            ) : null}
          </Flex>
        )}
      </CardContent>
    </Card>
  );
}
